import type { GetRequestsResponse } from "@nlc/shared-types";
import type { SortDescriptor } from "react-stately";

import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

import { requestListKeys } from "@/hooks/queryKeys";
import { useUser } from "@/hooks/useUser";
import apiClient from "@/lib/api/client";

import type { RequestFilterType } from "../../types/filters";

import { getRequestTypes } from "../../utils/requestUtils";
import { mapSortColumnToBackend } from "../../utils/sortingUtils";

interface UseQueuedRequestsParams {
  enabled?: boolean;
  filter?: RequestFilterType;
  limit?: number;
  page?: number;
  queueSection?: "detailsNeeded" | "inQueue" | "paused";
  search?: string;
  sortColumn?: string;
  sortDescriptor?: SortDescriptor;
  sortDirection?: "asc" | "desc";
}

export function useQueuedRequests(params: UseQueuedRequestsParams = {}) {
  const { data: userData } = useUser();
  const projectId = userData?.currentProjectId;
  const refetchRef = useRef<(() => void) | null>(null);

  const query = useQuery({
    enabled: !!userData?.user && (params.enabled ?? true), // Only enabled when user is authenticated and explicitly enabled
    gcTime: 5 * 60 * 1000, // 5 minutes garbage collection
    queryFn: async () => {
      // Build query parameters
      const queryParams: Record<string, number | string | string[]> = {
        direction: params.sortDescriptor
          ? params.sortDescriptor.direction === "descending"
            ? "desc"
            : "asc"
          : params.sortDirection || "desc",
        // Explicitly request all needed fields for the queued tab
        fields: [
          "id",
          "name",
          "request_type",
          "status",
          "created_by",
          "brand",
          "created_at",
          "design_type",
          "queue_position",
        ],
        sortBy: params.sortDescriptor
          ? mapSortColumnToBackend(String(params.sortDescriptor.column))
          : mapSortColumnToBackend(params.sortColumn || "createdAt"),
        tab: "queued",
      };

      // Only add pagination for sections other than "inQueue"
      // The "inQueue" section should return all results for drag-and-drop reordering
      if (params.queueSection !== "inQueue") {
        queryParams.limit = params.limit || 10; // Default pagination limit
        queryParams.page = params.page || 1;
        // Always get count from the data query, never from a skipped/cached count
        queryParams.skipCount = "false";
      }

      // Add projectId for customer account filtering
      if (projectId) {
        queryParams.projectId = projectId;
      }

      // Add queue section filter if provided
      if (params.queueSection) {
        queryParams.queueSection = params.queueSection;
      }

      // Add category filter if provided
      if (params.filter && params.filter !== "all") {
        const filterTypes = getRequestTypes(params.filter);

        queryParams.category = filterTypes;
      }

      // Add search if provided
      if (params.search) {
        queryParams.search = params.search;
      }

      return await apiClient
        .url("/requests/list")
        .query(queryParams)
        .get()
        .json<GetRequestsResponse>();
    },
    queryKey: [
      ...requestListKeys.all,
      "queued",
      projectId,
      params.queueSection,
      params.filter,
      params.limit,
      params.page,
      params.search,
      params.sortDescriptor?.column || params.sortColumn,
      params.sortDescriptor?.direction || params.sortDirection,
    ],
    refetchInterval: (data) =>
      data && document.visibilityState === "visible" ? 45 * 1000 : false,
    staleTime: 0,
  });

  // Store refetch function for visibility event handlers
  refetchRef.current = query.refetch;

  // Enhanced auto-refresh on visibility changes and window focus
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && refetchRef.current) {
        refetchRef.current();
      }
    };

    const handleFocus = () => {
      if (refetchRef.current) {
        refetchRef.current();
      }
    };

    // Add event listeners
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    // Cleanup event listeners
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, []);

  return query;
}
